import { useAudienceExplorerSearchStore } from "@/stores/AudienceExplorerSearchStore";
import { useTaskStore } from "@/stores/TaskStore";

export const AudienceExplorerStores = [useAudienceExplorerSearchStore];

export const AudienceExplorerChannel = {
  async received(payload) {
    // Export completion (report PPTX + per-geography CSV counts), success or
    // error, is handled by the shared export manager, which downloads the
    // standardized payload.data.files. Must run before the generic error branch
    // below (which returns early) so export loading state always clears.
    if (await this.audienceExplorerSearchStore.handleExportMessage(payload)) {
      return;
    }
    if (payload.status === "error") {
      const task = Object.keys(this.audienceExplorerSearchStore.taskIds).find(
        (task) => this.audienceExplorerSearchStore.taskIds[task] === payload.id,
      );
      if (task) {
        this.audienceExplorerSearchStore.loading[task] = false;
        this.audienceExplorerSearchStore.taskIds[task] = undefined;
        this.audienceExplorerSearchStore.errors[task] = payload.message;
      }
      return;
    }
    if (payload.id === this.audienceExplorerSearchStore.taskIds.mediaMix) {
      const processedMediaMix =
        await this.audienceExplorerSearchStore.processMediaMixData(
          payload.data,
        );
      if (processedMediaMix) {
        this.audienceExplorerSearchStore.mediaMix = processedMediaMix;
        this.audienceExplorerSearchStore.taskIds.mediaMix = undefined;
        this.audienceExplorerSearchStore.loading.mediaMix = false;
        this.audienceExplorerSearchStore.loading.mediaMixSelections = false;
      }
    }

    if (payload.id === this.audienceExplorerSearchStore.taskIds.marketRanker) {
      this.audienceExplorerSearchStore.marketRanker = payload.data;
      this.audienceExplorerSearchStore.taskIds.marketRanker = undefined;
      this.audienceExplorerSearchStore.loading.marketRanker = false;
    }
    if (payload.id === this.audienceExplorerSearchStore.taskIds.demographics) {
      this.audienceExplorerSearchStore.demographics = payload.data;
      this.audienceExplorerSearchStore.taskIds.demographics = undefined;
      this.audienceExplorerSearchStore.loading.demographics = false;
    }
    if (
      payload.id === this.audienceExplorerSearchStore.taskIds.audienceCounts
    ) {
      this.audienceExplorerSearchStore.audienceCounts = payload.data;
      this.audienceExplorerSearchStore.taskIds.audienceCounts = undefined;
      this.audienceExplorerSearchStore.loading.audienceCounts = false;
      if (payload.data.count === 0) {
        this.audienceExplorerSearchStore.error =
          "There are not enough individuals in this audience for analysis in Audience Explorer";
        cancelTasks();
      } else if (
        this.audienceExplorerSearchStore.comparisonAudience &&
        !this.audienceExplorerSearchStore.comparisonIncludedInExplore
      ) {
        this.audienceExplorerSearchStore.refetchComparisonData();
      }
    }
    if (payload.id === this.audienceExplorerSearchStore.taskIds.topTvPrograms) {
      this.audienceExplorerSearchStore.topTvPrograms =
        payload.data.ratings.slice(0, 10);
      this.audienceExplorerSearchStore.taskIds.topTvPrograms = undefined;
      this.audienceExplorerSearchStore.loading.topTvPrograms = false;
    }
    if (
      payload.id === this.audienceExplorerSearchStore.taskIds.linearStreaming
    ) {
      this.audienceExplorerSearchStore.linearStreaming = payload.data;
      this.audienceExplorerSearchStore.taskIds.linearStreaming = undefined;
      this.audienceExplorerSearchStore.loading.linearStreaming = false;
    }
    if (
      payload.id === this.audienceExplorerSearchStore.taskIds.behavioralInsights
    ) {
      this.audienceExplorerSearchStore.loading.behavioralInsights = false;
      const results = payload.data?.results;
      if (!results || results[0]?.status === "error") {
        const error =
          results[0]?.message || "No Behavioral Insights to Display";
        this.audienceExplorerSearchStore.errors.behavioralInsights = error;
        this.audienceExplorerSearchStore.behavioralInsights = undefined;
      } else {
        this.audienceExplorerSearchStore.behavioralInsights = results;
        this.audienceExplorerSearchStore.errors.behavioralInsights = undefined;
      }
      this.audienceExplorerSearchStore.taskIds.behavioralInsights = undefined;
    }
    if (payload.id === this.audienceExplorerSearchStore.taskIds.haloInsights) {
      this.audienceExplorerSearchStore.haloInsights =
        payload.data?.counts ?? [];
      this.audienceExplorerSearchStore.taskIds.haloInsights = undefined;
      this.audienceExplorerSearchStore.loading.haloInsights = false;
    }
    if (
      payload.id === this.audienceExplorerSearchStore.taskIds.policyInsights
    ) {
      this.audienceExplorerSearchStore.policyInsights = payload.data;
      this.audienceExplorerSearchStore.taskIds.policyInsights = undefined;
      this.audienceExplorerSearchStore.loading.policyInsights = false;
    }
    if (
      payload.id === this.audienceExplorerSearchStore.taskIds.consumerInsights
    ) {
      this.audienceExplorerSearchStore.consumerInsights = payload.data;
      this.audienceExplorerSearchStore.taskIds.consumerInsights = undefined;
      this.audienceExplorerSearchStore.loading.consumerInsights = false;
    }
    if (
      payload.id === this.audienceExplorerSearchStore.taskIds.activationSummary
    ) {
      this.audienceExplorerSearchStore.activationSummary = payload.data;
      this.audienceExplorerSearchStore.taskIds.activationSummary = undefined;
      this.audienceExplorerSearchStore.loading.activationSummary = false;
    }
    if (payload.id === this.audienceExplorerSearchStore.taskIds.aiPersona) {
      this.audienceExplorerSearchStore.aiPersona = payload.data;
      this.audienceExplorerSearchStore.taskIds.aiPersona = undefined;
      this.audienceExplorerSearchStore.loading.aiPersona = false;
    }
  },
};

export const cancelTasks = async () => {
  const audienceExplorerSearchStore = useAudienceExplorerSearchStore();
  const taskStore = useTaskStore();
  const tasks = [
    "consumerInsights",
    "policyInsights",
    "demographics",
    "behavioralInsights",
    "mediaMix",
    "linearStreaming",
    "topTvPrograms",
    "marketRanker",
    "activationSummary",
    "audienceCounts",
    "aiPersona",
    "haloInsights",
  ];
  const activeTasks = tasks.filter(
    (key) => audienceExplorerSearchStore.taskIds[key],
  );
  const cancel = activeTasks.map(async (key) => {
    const taskId = audienceExplorerSearchStore.taskIds[key];
    await taskStore.cancelTask(taskId);
    audienceExplorerSearchStore.taskIds[key] = undefined;
    audienceExplorerSearchStore.loading[key] = false;
    if (key === "mediaMix") {
      audienceExplorerSearchStore.loading.mediaMixSelections = false;
    }
  });
  Promise.all(cancel);
};
